import { Box, Button, Typography } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { forceRefresh } from "@/lib/versionManager";

/**
 * Shown when a route's chunk could not load because the site was redeployed
 * while this tab was open.
 *
 * `autoReloading` is the normal path: lazyWithRetry has already triggered a
 * hard reload, so this screen only explains the wait. If that reload never
 * lands it degrades into the manual screen after a few seconds.
 */
export function StaleVersionScreen({ autoReloading = false }: { autoReloading?: boolean }) {
  const prefersReducedMotion = useReducedMotion();
  const [isReloading, setIsReloading] = useState(false);
  const [autoFailed, setAutoFailed] = useState(false);

  useEffect(() => {
    if (!autoReloading) return;
    const timer = window.setTimeout(() => setAutoFailed(true), 7000);
    return () => window.clearTimeout(timer);
  }, [autoReloading]);

  const showAuto = autoReloading && !autoFailed;

  const handleReload = () => {
    if (isReloading) return;
    setIsReloading(true);
    forceRefresh();
  };

  const handleGoBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.assign("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--backgroundColor, #0b0b0b)",
        px: "1.5rem",
        py: "4rem",
      }}
    >
      <Box
        component={motion.section}
        initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: prefersReducedMotion ? 0 : 0.25, ease: [0.22, 1, 0.36, 1] }}
        role="status"
        aria-live="polite"
        sx={{
          width: "100%",
          maxWidth: "440px",
          textAlign: "center",
          borderRadius: "16px",
          border: "1px solid color-mix(in srgb, var(--logoColor, #d9c08a) 24%, transparent)",
          backgroundColor: "var(--color5, #141414)",
          padding: "28px 24px",
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            width: "44px",
            height: "44px",
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            color: "var(--logoColor, #d9c08a)",
            backgroundColor: "color-mix(in srgb, var(--logoColor, #d9c08a) 14%, transparent)",
            "& svg": { width: "20px", height: "20px" },
            "@media (prefers-reduced-motion: no-preference)": {
              "& svg": {
                animation: showAuto || isReloading ? "truevined-update-spin 1s linear infinite" : "none",
              },
            },
            "@keyframes truevined-update-spin": {
              from: { transform: "rotate(0deg)" },
              to: { transform: "rotate(360deg)" },
            },
          }}
        >
          <RefreshCw />
        </Box>

        <Typography
          component="h2"
          sx={{
            fontFamily: "Hanson, Poppins, sans-serif",
            color: "var(--logoColor, #d9c08a)",
            fontSize: { xs: "22px", md: "20px" },
            lineHeight: 1.25,
            letterSpacing: "0.02em",
            marginTop: "16px",
          }}
        >
          {showAuto ? "Getting the latest version" : "A new version of the site is ready"}
        </Typography>

        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            color: "var(--textColor, #e8e8e8)",
            fontSize: { xs: "15px", md: "14px" },
            lineHeight: 1.6,
            marginTop: "10px",
          }}
        >
          {showAuto
            ? "Loading the newest build of Truevined. This takes a moment."
            : "Truevined was updated while this tab was open, so this page could not load. Reload to get the latest."}
        </Typography>

        {!showAuto && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              justifyContent: "center",
              marginTop: "22px",
            }}
          >
            <Button
              type="button"
              onClick={handleReload}
              disabled={isReloading}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "15px", md: "14px" },
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "999px",
                padding: "10px 22px",
                minHeight: "44px",
                color: "var(--backgroundColor, #0b0b0b)",
                backgroundColor: "var(--logoColor, #d9c08a)",
                "&:hover": { backgroundColor: "var(--logoColor2, #f0dfb6)" },
                "&:focus-visible": { outline: "2px solid var(--logoColor2, #f0dfb6)", outlineOffset: "2px" },
                "&.Mui-disabled": { opacity: 0.7, color: "var(--backgroundColor, #0b0b0b)" },
              }}
            >
              {isReloading ? "Reloading" : "Reload"}
            </Button>
            <Button
              type="button"
              onClick={handleGoBack}
              disabled={isReloading}
              sx={{
                fontFamily: "Poppins, sans-serif",
                fontSize: { xs: "15px", md: "14px" },
                fontWeight: 500,
                textTransform: "none",
                borderRadius: "999px",
                padding: "10px 20px",
                minHeight: "44px",
                color: "var(--textColor, #e8e8e8)",
                border: "1px solid color-mix(in srgb, var(--logoColor, #d9c08a) 28%, transparent)",
                "&:hover": { backgroundColor: "color-mix(in srgb, var(--logoColor, #d9c08a) 10%, transparent)" },
                "&:focus-visible": { outline: "2px solid var(--logoColor2, #f0dfb6)", outlineOffset: "2px" },
              }}
            >
              Go back
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function VersionMismatchFallback() {
  return <StaleVersionScreen />;
}

export function StaleVersionAutoReload() {
  return <StaleVersionScreen autoReloading />;
}
